import * as React from 'react'

import * as m from 'motion/react-m'

import { Button, Flex, Text, UpgradeBadge } from '@buffer-mono/popcorn'

import type { FragmentOf, ResultOf } from '~publish/graphql'

import { SuggestionsPaywall } from './SuggestionsPaywall'
import { formatMentionUsername } from '../CommentReplyForm/utils/replyFormUtils'
import { useCommentData } from '../context/CommentContext'
import { EngagementReplySuggestions } from '../../components/EngagementReplySuggestions'
import { useSuggestionChipHoverMotion } from '../../components/EngagementReplySuggestions/suggestionChipMotion'
import type { EngagementReplySuggestions_SavedReply } from '../../components/EngagementReplySuggestions/EngagementReplySuggestions'
import { useCommentSuggestions } from '../../hooks/comments/useCommentSuggestions'
import type { getCommentSuggestionsQuery } from '../../hooks/comments/useCommentSuggestions'
import { useMostSimilarSavedReply } from '../../hooks/savedReplies/useMostSimilarSavedReply'
import { useCommentsTracking } from '../../hooks/tracking/useCommentsTracking'
import { isPrivateLinkedInAccount } from '../../util/privateAccount'

import styles from './CommentReplyFormHeader.module.css'

import type { JSX } from 'react'

/**
 * One AI suggestion as delivered by the `commentSuggestions` query, derived
 * from the {@link getCommentSuggestionsQuery} gql.tada op.
 */
type CommentSuggestion = NonNullable<
  NonNullable<
    ResultOf<typeof getCommentSuggestionsQuery>['commentSuggestions']
  >['suggestions']
>[number]

type Suggestion = CommentSuggestion & {
  id: string
}

const MotionButton = m.create(Button)

/**
 * Checks if a comment text is meaningful enough to generate AI suggestions
 * @param text The comment text to validate
 * @returns boolean indicating if the text is meaningful
 */
const isMeaningfulComment = (text: string): boolean => {
  // Check if the text contains any letters, numbers, or emojis
  // This includes:
  // - Basic Latin letters and numbers
  // - Emojis and other Unicode symbols
  // - Non-Latin scripts
  return /[\p{L}\p{N}\p{Emoji}]/u.test(text)
}

export const ReplyingToLabel = ({
  username,
}: {
  username: string
}): JSX.Element => (
  <Text size="sm" color="subtle" className={styles.replyingToText}>
    Replying to {formatMentionUsername(username)}
  </Text>
)

type CommentReplyFormHeaderProps = {
  /**
   * Callback when a suggestion is clicked
   */
  onSuggestionClick: (suggestionText: string) => boolean
  /**
   * Whether the channel supports AI reply suggestions. The "Replying to @user"
   * label always renders regardless; this only gates the AI suggestion chips
   * and the saved-reply similarity match (and the underlying fetch).
   */
  supportsSuggestions: boolean
}

/**
 * Header row above the reply textarea. Always renders the "Replying to @user"
 * label (it's reply identity, not an AI feature); when the channel supports AI
 * features it additionally fetches and renders the suggestion chips and the
 * most-similar saved reply alongside the label.
 *
 * Thin data-provider: it owns the comment-side data lifecycle (lazy fetch,
 * loading, paywall, tracking) and feeds the shared
 * `EngagementReplySuggestions` strip, which owns the rendering.
 */
export function CommentReplyFormHeader({
  onSuggestionClick,
  supportsSuggestions,
}: CommentReplyFormHeaderProps): JSX.Element {
  const comment = useCommentData()
  const [suggestions, setSuggestions] = React.useState<Suggestion[]>()
  const [isSuggestionsLoading, setIsSuggestionsLoading] = React.useState(false)
  const [isReachLimitError, setIsReachLimitError] =
    React.useState<boolean>(false)
  const { fetchCommentSuggestions } = useCommentSuggestions()
  const hoverMotion = useSuggestionChipHoverMotion()
  const { trackSuggestionUsed, trackSavedReplyUsed } = useCommentsTracking()

  const commentId = comment.id
  const commentText = comment.text
  const userRating = comment.metadata?.userRating

  const mostSimilarSavedReply = useMostSimilarSavedReply(commentText)

  React.useEffect(() => {
    // Don't fetch for channels without AI support — the "Replying to @user"
    // label still renders below, just without chips.
    if (!supportsSuggestions) return
    // Fetch suggestions if we have a commentId and either text or userRating
    const hasUserRating = userRating != null
    if (!commentId || (!commentText && !hasUserRating)) return

    /**
     * Fetch suggestions from AI
     */
    const fetchSuggestions = async (): Promise<void> => {
      setIsSuggestionsLoading(true)

      try {
        const { data, error } = await fetchCommentSuggestions({
          commentId,
          regenerate: false,
        })

        if (data) {
          // Generate unique IDs for each suggestion when received
          const suggestionsWithIds: Suggestion[] = data.map((s) =>
            Object.assign({}, s, { id: crypto.randomUUID() }),
          )
          setSuggestions(suggestionsWithIds)
        }
        if (error) {
          setIsReachLimitError(true)
        }
      } catch (error) {
        // Local diagnostic for a non-blocking transient failure (rate limit, network);
        // intentionally not routed to Sentry to avoid noise.
        // eslint-disable-next-line no-console
        console.error('Error fetching suggestions:', error)
      } finally {
        setIsSuggestionsLoading(false)
      }
    }

    void fetchSuggestions()
  }, [
    supportsSuggestions,
    commentId,
    commentText,
    userRating,
    fetchCommentSuggestions,
  ])

  const handleSuggestionClick = React.useCallback(
    (suggestionText: string): boolean => {
      const wasInserted = onSuggestionClick(suggestionText)

      trackSuggestionUsed({
        comment,
        suggestionText,
      })

      return wasInserted
    },
    [comment, onSuggestionClick, trackSuggestionUsed],
  )

  const handleSavedReplyClick = React.useCallback(
    (
      savedReply: FragmentOf<typeof EngagementReplySuggestions_SavedReply>,
    ): boolean => {
      const wasInserted = onSuggestionClick(savedReply.content.text)

      trackSavedReplyUsed({
        comment,
        savedReply,
      })

      return wasInserted
    },
    [comment, onSuggestionClick, trackSavedReplyUsed],
  )

  const isPrivateAccount = isPrivateLinkedInAccount({
    serviceType: comment.serviceType,
    comment,
  })

  const replyingToLabel = !isPrivateAccount && (
    <ReplyingToLabel username={comment.author.username} />
  )

  // Check if we should request suggestions
  const hasUserRating = comment.metadata?.userRating != null
  const hasMeaningfulText = commentText && isMeaningfulComment(commentText)
  const shouldRequestSuggestions =
    commentId && (hasMeaningfulText || hasUserRating)

  // No AI chips to show (unsupported channel or nothing worth suggesting) —
  // render just the "Replying to @user" label via the shared strip's fallback.
  if (!supportsSuggestions || !shouldRequestSuggestions) {
    return (
      <EngagementReplySuggestions
        replyingToLabel={replyingToLabel}
        suggestions={[]}
        mostSimilarSavedReply={null}
        onSuggestionClick={handleSuggestionClick}
        onSavedReplyClick={handleSavedReplyClick}
      />
    )
  }

  if (isReachLimitError) {
    return (
      <Flex direction="row" align="center" fullWidth>
        {replyingToLabel}
        <SuggestionsPaywall
          ctaProperties={{
            location: 'community',
            button: 'generateReplySuggestions',
          }}
        >
          <MotionButton
            variant="tertiary"
            size="small"
            className={styles.generateReplySuggestions}
            {...hoverMotion}
          >
            Unlock Reply Suggestions
            <UpgradeBadge size="small" />
          </MotionButton>
        </SuggestionsPaywall>
      </Flex>
    )
  }

  return (
    <EngagementReplySuggestions
      replyingToLabel={replyingToLabel}
      suggestions={suggestions ?? []}
      isLoading={isSuggestionsLoading}
      mostSimilarSavedReply={mostSimilarSavedReply}
      onSuggestionClick={handleSuggestionClick}
      onSavedReplyClick={handleSavedReplyClick}
    />
  )
}
